import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import './ViewerPage.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || window.location.origin;

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "turn:46.225.89.120:3478", username: "sentinel", credential: "sentinel2024" },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export default function ViewerPage({ onBack }) {
  const [cameras, setCameras] = useState([]);
  const [streams, setStreams] = useState({}); // camId -> MediaStream
  const [alerts, setAlerts] = useState([]);
  const [clips, setClips] = useState([]);
  const [tab, setTab] = useState('live'); // live | clips | alerts
  const [socketConnected, setSocketConnected] = useState(false);
  const [activeCamera, setActiveCamera] = useState(null); // for fullscreen view

  const socketRef = useRef(null);
  const peersRef = useRef({}); // camId -> RTCPeerConnection
  const videoRefs = useRef({}); // camId -> video element

  const addAlert = useCallback((camId, camName) => {
    const alert = {
      id: Date.now(),
      camId,
      camName,
      time: new Date().toLocaleTimeString('es-ES'),
      date: new Date().toLocaleDateString('es-ES'),
    };
    setAlerts(prev => [alert, ...prev].slice(0, 50));

    // Browser notification
    if (Notification.permission === 'granted') {
      new Notification(`🚨 Movimiento detectado`, {
        body: `Cámara: ${camName}`,
        icon: '/favicon.ico',
      });
    }
  }, []);

  useEffect(() => {
    // Request notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      socket.emit('register-viewer');
    });

    socket.on('disconnect', () => setSocketConnected(false));

    socket.on('camera-list', (list) => {
      setCameras(list);
      // Auto-connect to all cameras
      list.forEach(cam => requestStream(cam.camId, socket));
    });

    socket.on('camera-online', ({ camId, camName }) => {
      setCameras(prev => {
        if (prev.find(c => c.camId === camId)) return prev;
        return [...prev, { camId, camName, online: true }];
      });
      requestStream(camId, socket);
    });

    socket.on('camera-offline', ({ camId }) => {
      setCameras(prev => prev.filter(c => c.camId !== camId));
      setStreams(prev => { const s = { ...prev }; delete s[camId]; return s; });
      if (peersRef.current[camId]) {
        peersRef.current[camId].close();
        delete peersRef.current[camId];
      }
    });

    socket.on('motion-alert', ({ camId, camName }) => {
      addAlert(camId, camName);
    });

    socket.on('new-clip', (clip) => {
      setClips(prev => [clip, ...prev].slice(0, 50));
    });

    // WebRTC signaling
    socket.on('offer', async ({ from, offer }) => {
      const camId = from; // in camera→viewer flow, from = camera's viewerId context
      // Actually in our flow: camera sends offer TO viewer
      // from = camera socket id, we need camId
      // We store peer by 'from' socket id temporarily
      const pc = createPeerConnection(from, socket);
      peersRef.current[`socket_${from}`] = pc;

      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('answer', { to: from, answer });
    });

    socket.on('ice-candidate', async ({ from, candidate }) => {
      const pc = peersRef.current[`socket_${from}`] || Object.values(peersRef.current)[0];
      if (pc && candidate) {
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
      }
    });

    // Load existing clips
    fetchClips();

    return () => {
      socket.disconnect();
      Object.values(peersRef.current).forEach(pc => pc.close());
    };
  }, []);

  function requestStream(camId, socket) {
    socket.emit('request-stream', { camId });
  }

  function createPeerConnection(fromSocketId, socket) {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) socket.emit('ice-candidate', { to: fromSocketId, candidate });
    };

    pc.ontrack = (event) => {
      const stream = event.streams[0];
      // Find which camera this belongs to — match by socket ID
      // We'll update streams with a temporary key, camera will register
      setStreams(prev => ({ ...prev, [fromSocketId]: stream }));

      // If there's a video element waiting for this
      Object.entries(videoRefs.current).forEach(([camId, el]) => {
        if (el && !el.srcObject) el.srcObject = stream;
      });
    };

    return pc;
  }

  async function fetchClips() {
    try {
      const res = await fetch(`${SERVER_URL}/api/clips`);
      const data = await res.json();
      setClips(data);
    } catch {}
  }

  async function deleteClip(fileName) {
    try {
      await fetch(`${SERVER_URL}/api/clips/${fileName}`, { method: 'DELETE' });
      setClips(prev => prev.filter(c => c.fileName !== fileName));
    } catch {}
  }

  const streamValues = Object.values(streams);

  return (
    <div className="viewer-page">
      <div className="viewer-bg" />

      {/* Header */}
      <header className="viewer-header">
        <button className="back-btn" onClick={onBack}>← ATRÁS</button>

        <div className="viewer-title-group">
          <span className="viewer-title">PANEL DE CONTROL</span>
          <span className="viewer-subtitle">SENTINEL SECURITY</span>
        </div>

        <div className="header-right">
          {alerts.length > 0 && (
            <div className="alert-indicator" onClick={() => setTab('alerts')}>
              <span className="dot red" />
              <span>{alerts.length} ALERTAS</span>
            </div>
          )}
          <div className="conn-badge">
            <span className={`dot ${socketConnected ? 'green' : 'gray'}`} />
            <span>{socketConnected ? 'CONECTADO' : 'DESCONECTADO'}</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="viewer-tabs">
        <button
          className={`tab ${tab === 'live' ? 'active' : ''}`}
          onClick={() => setTab('live')}
        >
          📹 EN VIVO
          {cameras.length > 0 && <span className="tab-badge">{cameras.length}</span>}
        </button>
        <button
          className={`tab ${tab === 'clips' ? 'active' : ''}`}
          onClick={() => { setTab('clips'); fetchClips(); }}
        >
          🎞️ GRABACIONES
          {clips.length > 0 && <span className="tab-badge">{clips.length}</span>}
        </button>
        <button
          className={`tab ${tab === 'alerts' ? 'active' : ''}`}
          onClick={() => setTab('alerts')}
        >
          🚨 ALERTAS
          {alerts.length > 0 && <span className="tab-badge alert-badge">{alerts.length}</span>}
        </button>
      </div>

      {/* Content */}
      <div className="viewer-content">

        {/* LIVE TAB */}
        {tab === 'live' && (
          <div className="live-tab">
            {cameras.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📡</div>
                <h3>Sin cámaras conectadas</h3>
                <p>Abre la app en el dispositivo cámara y selecciona "Modo Cámara"</p>
                <div className="empty-url">
                  URL: <span>{window.location.origin}?mode=camera</span>
                </div>
              </div>
            ) : (
              <div className={`cameras-grid cams-${Math.min(cameras.length, 4)}`}>
                {cameras.map((cam) => (
                  <CameraFeed
                    key={cam.camId}
                    cam={cam}
                    stream={streams[cam.camId]}
                    onFullscreen={() => setActiveCamera(cam)}
                    videoRefs={videoRefs}
                    streams={streams}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* CLIPS TAB */}
        {tab === 'clips' && (
          <div className="clips-tab">
            {clips.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🎞️</div>
                <h3>Sin grabaciones</h3>
                <p>Las grabaciones aparecen cuando se detecta movimiento</p>
              </div>
            ) : (
              <div className="clips-grid">
                {clips.map((clip, i) => (
                  <div key={i} className="clip-card animate-in">
                    <div className="clip-header">
                      <span className="clip-cam">{clip.camId}</span>
                      <button
                        className="clip-delete"
                        onClick={() => deleteClip(clip.fileName)}
                      >✕</button>
                    </div>
                    <video
                      src={`${SERVER_URL}${clip.url}`}
                      controls
                      className="clip-video"
                    />
                    <div className="clip-meta">
                      <span>{new Date(clip.timestamp).toLocaleString('es-ES')}</span>
                      <span>{(clip.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ALERTS TAB */}
        {tab === 'alerts' && (
          <div className="alerts-tab">
            {alerts.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">✅</div>
                <h3>Sin alertas</h3>
                <p>Todo tranquilo. Las alertas aparecerán aquí cuando se detecte movimiento.</p>
              </div>
            ) : (
              <div className="alerts-list">
                <div className="alerts-header">
                  <span>{alerts.length} alertas registradas</span>
                  <button className="clear-btn" onClick={() => setAlerts([])}>
                    LIMPIAR TODO
                  </button>
                </div>
                {alerts.map(alert => (
                  <div key={alert.id} className="alert-item animate-in">
                    <div className="alert-left">
                      <span className="dot red" />
                      <div>
                        <div className="alert-cam">{alert.camName || alert.camId}</div>
                        <div className="alert-time">{alert.date} — {alert.time}</div>
                      </div>
                    </div>
                    <div className="alert-tag">MOVIMIENTO</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {activeCamera && (
        <div className="fullscreen-overlay" onClick={() => setActiveCamera(null)}>
          <div className="fullscreen-content" onClick={e => e.stopPropagation()}>
            <button className="fs-close" onClick={() => setActiveCamera(null)}>✕</button>
            <div className="fs-label">{activeCamera.camName || activeCamera.camId}</div>
            <CameraFeed
              cam={activeCamera}
              stream={streams[activeCamera.camId]}
              fullscreen
              videoRefs={videoRefs}
              streams={streams}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Camera Feed Component ──────────────────────────────────────────────────────
function CameraFeed({ cam, stream, onFullscreen, fullscreen, videoRefs, streams }) {
  const videoEl = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (videoEl.current) {
      videoRefs.current[cam.camId] = videoEl.current;
      // Try to find matching stream
      const s = streams[cam.camId] || Object.values(streams)[0];
      if (s) { videoEl.current.srcObject = s; videoEl.current.onplaying = () => setPlaying(true); }
    }
  }, [cam.camId, streams]);

  return (
    <div className={`camera-feed ${fullscreen ? 'fs-feed' : ''}`}>
      <div className="feed-top-bar">
        <div className="feed-info">
          <span className="dot green" />
          <span className="feed-name">{cam.camName || cam.camId}</span>
        </div>
        <div style={{display:"flex",gap:"4px",marginRight:"8px"}}>
          {[0,90,180,270].map(r=>(<button key={r} onClick={()=>setRotation(r)} style={{padding:"2px 5px",fontSize:"10px",background:rotation===r?"#00ff88":"#1a2030",color:rotation===r?"#050608":"#7a8899",border:"1px solid #2a3550",borderRadius:"2px",cursor:"pointer"}}>{r}°</button>))}
        </div>
        {!fullscreen && (
          <button className="feed-expand" onClick={onFullscreen}>⤢</button>
        )}
      </div>

      <div className="feed-video-wrap">
        <video
          ref={videoEl}
          autoPlay
          playsInline
          className="feed-video"
        style={{ transform: `rotate(${rotation}deg) scale(${rotation === 90 || rotation === 270 ? 0.56 : 1})`, transition: "transform 0.3s ease", width: "100%", height: "100%", objectFit: "cover" }}
        />
        {!playing && (
          <div className="feed-connecting">
            <div className="connecting-spinner" />
            <span>Conectando...</span>
          </div>
        )}
        <div className="feed-grid-overlay" />
      </div>

      <div className="feed-footer">
        <span className="feed-id">{cam.camId}</span>
        <span className="feed-live">● EN VIVO</span>
      </div>
    </div>
  );
}
